/**
 * One CASE in the queue: a card headed by the case identity with its
 * outstanding actions listed inside — always a card, whether the case
 * has one thing to do or five. (The previous grammar collapsed a
 * single-action case into a bare row and only stacked multi-action
 * cases under a caption, so the same case changed shape as items came
 * and went.)
 *
 *   ┌──────────────────────────────────────────────────────────────┐
 *   │ CODENAME · Client            [Expedited]   2 actions  [owner] │
 *   │ Subjects: Acme Ltd, J. Doe                                   │
 *   │ ● (chip) what needs doing · £1,500       [due] [assignee] 2h │
 *   │   (chip) another action                          [3d]     1d │
 *   └──────────────────────────────────────────────────────────────┘
 *
 * The owner avatar is the family-wide claim / assign menu; rows
 * select into the preview pane.
 */

import { Badge, Card, Group, Stack, Text } from '@mantine/core';

import { InboxAnalyst, InboxItem } from '@/types/inbox';
import { AnalystAvatar, OwnerAvatarMenu, QueueRow, RowOwnerContext } from './QueueRow';

export interface CaseCardIdentity {
  codename: string;
  clientName: string | null;
  clientCode?: string | null;
  expedited?: boolean;
  /** Deduped subject names (capped server-side) + the uncapped count. */
  subjectNames?: string[];
  subjectTotal?: number;
}

interface CaseCardProps {
  identity: CaseCardIdentity;
  /** The case owner shown on the header. */
  analyst: InboxAnalyst | null;
  /** When present the header avatar becomes the claim/assign menu. */
  owner: RowOwnerContext | null;
  /** The outstanding actions, most urgent first. */
  items: InboxItem[];
  selectedId: string | null;
  /** Unseen-by-anyone marker per item. */
  isNew: (item: InboxItem) => boolean;
  onSelect: (item: InboxItem) => void;
}

export function CaseCard({
  identity,
  analyst,
  owner,
  items,
  selectedId,
  isNew,
  onSelect,
}: Readonly<CaseCardProps>) {
  const containsSelected = selectedId !== null && items.some((i) => i.id === selectedId);
  const subjects = identity.subjectNames ?? [];
  const subjectTotal = identity.subjectTotal ?? subjects.length;
  const hidden = subjectTotal - subjects.length;

  return (
    <Card
      withBorder
      radius="md"
      padding={0}
      mx="sm"
      mb="xs"
      data-testid="queue-card"
      data-selected={containsSelected || undefined}
      style={{
        borderColor: containsSelected ? 'var(--mantine-color-yellow-5)' : undefined,
        overflow: 'hidden',
      }}
    >
      <Group
        gap={6}
        wrap="nowrap"
        px="sm"
        pt={8}
        pb={4}
        style={{ minWidth: 0 }}
        data-testid="queue-card-header"
      >
        <Text size="sm" fw={600} style={{ whiteSpace: 'nowrap' }}>
          {identity.codename}
        </Text>
        {identity.clientName && (
          <Text size="xs" fw={500} c="dimmed" truncate="end" style={{ minWidth: 0 }}>
            · {identity.clientName}
          </Text>
        )}
        {identity.expedited && (
          <Badge size="xs" variant="light" color="orange" style={{ flex: 'none' }}>
            Expedited
          </Badge>
        )}
        <Text size="xs" c="dimmed" ml="auto" style={{ whiteSpace: 'nowrap', flex: 'none' }}>
          {items.length} {items.length === 1 ? 'action' : 'actions'}
        </Text>
        {owner ? (
          <OwnerAvatarMenu analyst={analyst} owner={owner} />
        ) : (
          <AnalystAvatar analyst={analyst} />
        )}
      </Group>
      {subjects.length > 0 && (
        <Text size="xs" c="dimmed" px="sm" pb={4} truncate="end" data-testid="queue-card-subjects">
          {subjects.join(', ')}
          {hidden > 0 ? ` +${hidden} more` : ''}
        </Text>
      )}
      <Stack gap={0} style={{ borderTop: '1px solid var(--mantine-color-default-border)' }}>
        {items.map((item) => (
          <QueueRow
            key={item.id}
            item={item}
            familyKey={identity.codename}
            selected={selectedId === item.id}
            isNew={isNew(item)}
            onSelect={onSelect}
          />
        ))}
      </Stack>
    </Card>
  );
}
